import { AdminListPageConfig, AdminListPageRenderer, TabDef, useAdminListPage } from 'hooks/useAdminListPage';
import { simpleDateAtTime } from 'lib/date';
import { AdminWorkflowRun, STUCK_REASON_LABELS } from 'models/AdminWorkflowRun';
import { adminIndexClients } from 'services/api/admin/clients';
import { adminWorkflowRunsQueryKeys, indexAdminWorkflowRuns } from 'services/api/admin/workflowRuns';

import { ProgressBar, RunId, RunStatus, Subtitle, Warned } from './runPresentation';

// Admin → Workflows → Runs. The support and engineering view of what is actually
// running, across every client and not scoped to the viewer's own facilities — which is the whole
// reason it exists, since the referral list cannot answer a question about somebody else's client.
const config: AdminListPageConfig<AdminWorkflowRun> = {
  title: 'Workflow Runs',
  columnStorageKey: 'admin.workflowRuns',
  queryKey: adminWorkflowRunsQueryKeys.index(),
  fetchFn: indexAdminWorkflowRuns,
  fetchParams: { defaultSort: 'created_at desc', pageSize: 50 },
  showResultCount: true,
  searchPlaceholder: 'Search runs',
  filters: [
    {
      key: 'clientId',
      label: 'Client',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminClients', 'workflowRuns', 'filter'],
        fetchFn: adminIndexClients,
        params: { sortBy: 'name asc' },
      },
      getOptionLabel: (option: any) => option.name,
      getOptionValue: (option: any) => option.id,
    },
    {
      key: 'definitionApiName',
      label: 'Workflow',
      type: 'dropdown',
      staticOptions: [
        { id: 'referral_intake', name: 'Inbound Referrals' },
        { id: 'portal_automation', name: 'Portal Automation' },
        { id: 'portal_discovery', name: 'Portal Discovery' },
      ],
      getOptionLabel: (option: any) => option.name,
      getOptionValue: (option: any) => option.id,
    },
    // Stuck or not, with no way to narrow by WHICH reason: the reason is printed on every row, so
    // a second control for it only pays at a volume of stuck runs that would itself be the
    // emergency. The API still takes `stuck_reason` and Stuck::Scope still carries the precedence
    // logic behind it, so putting this back is one entry in this list.
    { key: 'stuck', label: 'Stuck only', type: 'checkbox' },
  ],

  columns: [
    {
      id: 'run',
      header: 'Run',
      size: 260,
      render: (row) => (
        <div>
          {/* The name leads, because a support question is always about a person and never about a
              run id. The id promotes to the top line when no name has been written yet, rather
              than leaving an apologetic placeholder on a row that is otherwise fine. */}
          <div>
            {row.patientName ? (
              <>
                {row.patientName}
                <RunId>{row.id.slice(0, 8)}</RunId>
              </>
            ) : (
              row.id.slice(0, 8)
            )}
          </div>
          {/* A stuck reason means something is wrong and is called out; a long wait on a person is
              ordinary, so it is stated plainly rather than warned about. */}
          {row.stuckReason ? (
            <Warned>{STUCK_REASON_LABELS[row.stuckReason]}</Warned>
          ) : (
            <Subtitle>{waitingFor(row) ?? row.facilityNames[0] ?? '—'}</Subtitle>
          )}
        </div>
      ),
    },
    { id: 'clientName', header: 'Client', size: 180, render: (row) => row.clientName ?? '—' },
    {
      id: 'workflowName',
      header: 'Workflow',
      size: 170,
      // No version here. Runs pin to the contract they started on and never move, so being
      // several versions back is the ordinary state of almost every open run, not a fault — and
      // an orange "N behind" on most rows reads as a list of broken things. Where the version
      // actually explains something, the run's own panel already says it: "no step on this run
      // produces it".
      render: (row) => row.workflowName,
    },
    { id: 'progress', header: 'Progress', size: 190, render: (row) => <ProgressBar steps={row.steps} /> },
    {
      id: 'currentStatus',
      header: 'Current Status',
      size: 150,
      render: (row) => <RunStatus run={row} />,
    },
    {
      id: 'createdAt',
      header: 'Started',
      size: 170,
      sortable: true,
      render: (row) => <Subtitle>{simpleDateAtTime(row.createdAt)}</Subtitle>,
    },
  ],

  // Clicking the row opens it, rather than a menu whose only item was "Open run".
  onRowClick: (row, ctx) => ctx.navigate(`/admin/workflows/runs/${row.id}`),
};

// How long the oldest step has been waiting on someone, in the words the rest of the app uses for
// elapsed time.
function waitingFor(row: AdminWorkflowRun) {
  if (!row.awaitingSince) return null;
  const days = Math.floor((Date.now() - new Date(row.awaitingSince).getTime()) / 86_400_000);

  if (days < 1) return 'Waiting on a person';

  return `Waiting on a person · ${days} day${days === 1 ? '' : 's'}`;
}

// The two tabs the ticket describes. Health had no link to it at all — the only way in was typing
// the URL — and `AdminListPageRenderer` already renders this shape for Clients.
export const RUN_TABS: TabDef[] = [
  { label: 'Runs', path: '/admin/workflows/runs' },
  { label: 'Health', path: '/admin/workflows/runs/health' },
];

export default function WorkflowRuns() {
  const state = useAdminListPage<AdminWorkflowRun>(config);

  return (
    <AdminListPageRenderer
      config={config}
      state={state}
      tabs={{ pageTitle: 'Workflow Runs', tabDefs: RUN_TABS, currentTabLabel: 'Runs' }}
    />
  );
}
